import { useEffect, useRef, useState, useCallback } from 'react';
import { useSelector } from 'react-redux';

import { history } from 'routes';
import { usePhotoSeriesInternalStore } from './root.hook';
import { CameraPhoto, FACING_MODES } from 'utils/camera-photo/index';
import { isAppleIOS } from 'utils/devices.util';
import {
  genericErrorNumberDenotation,
  genericErrors,
  idealHeightResolution,
  idealResolution,
  idealWidthResolution,
  isScreenPortrait,
  logErrorToServer,
  lowResolution
} from 'utils';
import { TRANSLATIONS_VALUES_KEYS } from 'redux/internationalization/internationalization';
import { selectUseNativeCamera } from 'redux/root.selectors';
import { IDealResolution } from 'utils/camera-photo/media-services';
import { CameraMode } from './root';
import { postDeviceInfo, useTranslate } from 'services';
import { useCurrentUser } from 'hooks';

const { vehicle_scan } = TRANSLATIONS_VALUES_KEYS;

const handleCameraErrorContinueClick = () => {
  history.goBack();
};

export function useRootProvider() {
  const [isCameraActive, setIsCameraActive] = useState<boolean>(false);

  const videoRef = useRef<null | HTMLVideoElement>(null);
  const fileInputRef = useRef<null | HTMLInputElement>(null);

  const useNativeCamera = useSelector(selectUseNativeCamera);
  const state = usePhotoSeriesInternalStore(useNativeCamera ? CameraMode.Native : CameraMode.InBrowser);
  const [cameraRestarted, setCameraRestarted] = useState<boolean>(false);

  const { translateToString } = useTranslate();

  const {
    captureImage: { cameraPhoto, cameraSettings },
    camera: { mode: cameraMode, fileInput },
    dispatchSetCaptureImageGenericError,
    dispatchSetCaptureImageCameraSettings,
    dispatchSetCaptureImageCameraPhoto,
    dispatchSetCameraFileInput
  } = state;

  const currentUser = useCurrentUser()!;

  const onError = useCallback((error: string) => {
    const goBack = translateToString(vehicle_scan.go_back_btn);
    const problemsHeader = translateToString(vehicle_scan.problems_header);

    dispatchSetCaptureImageGenericError({
      importance: 1,
      text: goBack,
      title: problemsHeader,
      onAction: handleCameraErrorContinueClick,
      errors: [{ error: genericErrorNumberDenotation.genericError, errorDetails: error }],
    });
  }, [dispatchSetCaptureImageGenericError, translateToString]);

  const onCameraStartError = useCallback((error: any) => {
    if (isCameraActive) setIsCameraActive(false);

    // Camera is not readable
    onError(genericErrors.cameraNotReadable);

    logErrorToServer('Camera start error', error);
  }, [isCameraActive, onError]);

  const startCamera = useCallback((cameraPhoto: CameraPhoto, idealResolution: IDealResolution) => {
    cameraPhoto
      .startCamera(FACING_MODES.ENVIRONMENT, currentUser.photoSeries.id, idealResolution)
      .then(() => {
        dispatchSetCaptureImageCameraSettings(cameraPhoto.getCameraSettings());
      })
      .catch((error) => onCameraStartError(error));

    dispatchSetCaptureImageCameraPhoto(cameraPhoto);
  }, [dispatchSetCaptureImageCameraPhoto, dispatchSetCaptureImageCameraSettings, onCameraStartError, currentUser.photoSeries.id]);

  const stopCamera = useCallback(() => {
    cameraPhoto?.stopCamera().catch((error: any) => logErrorToServer('Camera stop error', error));
  }, [cameraPhoto]);

  useEffect(() => {
    if (cameraMode === CameraMode.Native) {
      postDeviceInfo(null, 'native', currentUser.photoSeries.id);
    }
  }, [cameraMode, currentUser.photoSeries.id]);

  useEffect(() => {
    if (cameraMode !== CameraMode.InBrowser) return;
    if (cameraPhoto || !videoRef.current || isCameraActive) return;

    const cameraPhotoObj = new CameraPhoto(videoRef.current!);
    startCamera(cameraPhotoObj, idealResolution);

    return () => {
      stopCamera();
    };
  }, [cameraPhoto, isCameraActive, onError, currentUser, startCamera, cameraMode, stopCamera]);

  /**
   * Fixes camera orientation mismatch on Samsung devices in landscape mode.
   *
   * Samsung phones (A6, etc.) don't properly handle 4:3 camera streams when rotated
   * to landscape. This function detects when screen is landscape but camera stream
   * remains portrait-oriented, then restarts with landscape-compatible resolution.
   *
   * @function handleCameraPortraitInLandscapeMode
   *
   * @behavior
   * - Skips non-browser modes and iPhones (they handle orientation properly)
   * - Detects mismatch: landscape screen + portrait camera stream
   * - Restarts camera with idealWidthResolution/idealHeightResolution
   * - Prevents restart loops with cameraRestarted flag
   *
   * @triggers Screen orientation changes, component updates
   * @sideEffects Brief camera interruption during restart
   */
  const handleCameraPortraitInLandscapeMode = useCallback(() => {
    if (cameraMode !== CameraMode.InBrowser) return;
    if (!cameraPhoto || cameraRestarted) return;

    if (isAppleIOS()) return;

    const isScreenLandscape = !isScreenPortrait();

    if (isScreenLandscape && cameraSettings && cameraPhoto.stream) {
      const streamSettings = cameraPhoto.stream.getVideoTracks()[0].getSettings();
      const height = streamSettings.height!;
      const width = streamSettings.width!;

      const isCameraPortrait = height > width;
      const restartCamera = isScreenLandscape && isCameraPortrait;

      if (restartCamera) {
        const highResolution = {
          width: idealWidthResolution,
          height: idealHeightResolution,
        };

        // First try to start with higher resolution (supports newer devices with higher resolution)
        cameraPhoto
          .startCamera(FACING_MODES.ENVIRONMENT, currentUser.photoSeries.id, highResolution)
          .then(() => {
            const cameraSettings = cameraPhoto.getCameraSettings();
            const newStreamSettings = cameraPhoto.stream!.getVideoTracks()[0].getSettings();

            // If the stream is still portrait, restart with low resolution (Samsung A6)
            if (newStreamSettings.width! > newStreamSettings.height!) {
              dispatchSetCaptureImageCameraSettings(cameraSettings);
              dispatchSetCaptureImageCameraPhoto(cameraPhoto);
            } else {
              startCamera(cameraPhoto, lowResolution);
            }
          })
          .catch((error) => onCameraStartError(error));

        setCameraRestarted(true);
      }
    }
  }, [
    cameraPhoto,
    cameraRestarted,
    cameraMode,
    cameraSettings,
    currentUser,
    dispatchSetCaptureImageCameraPhoto,
    dispatchSetCaptureImageCameraSettings,
    onCameraStartError,
    startCamera
  ]);

  useEffect(() => handleCameraPortraitInLandscapeMode(), [handleCameraPortraitInLandscapeMode]);
  useEffect(() => {
    if (window.screen.orientation) {
      window.screen.orientation.addEventListener('change', handleCameraPortraitInLandscapeMode);
    } else {
      window.addEventListener('orientationchange', handleCameraPortraitInLandscapeMode);
    }

    return () => {
      if (window.screen.orientation) {
        window.screen.orientation.removeEventListener('change', handleCameraPortraitInLandscapeMode);
      } else {
        window.removeEventListener('orientationchange', handleCameraPortraitInLandscapeMode);
      }
    };
  }, [handleCameraPortraitInLandscapeMode]);

  useEffect(() => {
    const fileRef = fileInputRef.current;
    if (cameraMode !== CameraMode.Native || fileInput || !fileRef) return;

    // Add HTML file input current ref to PhotoSeriesStoreContext for native camera usage
    dispatchSetCameraFileInput(fileRef);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cameraMode, fileInputRef.current, fileInput]);

  // CLOSE CAMERA SOLUTION PART 1: Listen to backgrounding / tab switch events and stop the camera stream if tab is hidden
  useEffect(() => {
    const handleVisibilityChange = () => {
      // There is no JS camera stream in native mode, so we don't need to stop it
      if (cameraMode === CameraMode.Native) return;

      if (document.hidden) {
        stopCamera();
      } else {
        startCamera(cameraPhoto as CameraPhoto, idealResolution);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [cameraPhoto, startCamera, stopCamera, cameraMode]);

  // CLOSE CAMERA SOLUTION PART 2: Listen to tab closing, navigation events and stop the camera stream
  useEffect(() => {
    const stopCameraSafe = () => {
      // There is no JS camera stream in native mode, so we don't need to stop it
      if (cameraMode === CameraMode.Native) return;
      stopCamera();
    };

    window.addEventListener('beforeunload', stopCameraSafe);
    window.addEventListener('pagehide', stopCameraSafe);

    return () => {
      window.removeEventListener('beforeunload', stopCameraSafe);
      window.removeEventListener('pagehide', stopCameraSafe);
    };
  }, [stopCamera, cameraMode]);

  return { isCameraActive, videoRef, state, idealResolution, fileInputRef };
}
